import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Radio, User } from "lucide-react";
import { useEffect } from "react";
import { StationCard } from "@/components/StationCard";
import { apiClient } from "@/lib/api-client";
import { getSoundEffects, initSoundEffects } from "@/services/SoundEffects";

export const Route = createFileRoute("/_layout/$username/")({
	component: UserProfile,
	loader: async ({ params }) => {
		// Exclude common non-username paths
		const excludedPaths = [
			"favicon.ico",
			"robots.txt",
			"sitemap.xml",
			"api",
			"static",
			"assets",
		];
		const hasFileExtension =
			params.username.includes(".") &&
			params.username.split(".").pop()?.length === 3;

		if (excludedPaths.includes(params.username) || hasFileExtension) {
			throw new Error("User not found");
		}

		try {
			const data = await apiClient.users.getStationsByUsername(params.username);
			return { profile: data, error: null };
		} catch (error) {
			return {
				profile: null,
				error: error instanceof Error ? error.message : "Unknown error",
			};
		}
	},
	head: ({ params, loaderData }) => {
		const profile = loaderData?.profile;
		const username = params.username;
		const title = `@${username} - Macrowave`;

		const description = profile
			? `Listen to @${username}'s radio stations on Macrowave. ${profile.stations.length} stations available.`
			: "User profile on Macrowave";

		return {
			meta: [
				{
					property: "og:title",
					content: title,
				},
				{
					property: "og:description",
					content: description,
				},
				{
					property: "og:type",
					content: "profile",
				},
				{
					name: "twitter:card",
					content: "summary",
				},
				{
					name: "twitter:title",
					content: title,
				},
				{
					name: "twitter:description",
					content: description,
				},
			],
		};
	},
});

function UserProfile() {
	const navigate = useNavigate();
	const { profile, error } = Route.useLoaderData();
	const { username } = Route.useParams();
	console.log(profile, error);

	// Initialize sound effects
	useEffect(() => {
		initSoundEffects();
	}, []);

	// Handle user not found
	if (error) {
		return (
			<div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
				<h1 className="mb-2 font-serif-600 text-2xl text-black">
					User Not Found
				</h1>
				<p className="mb-6 font-serif text-gray-600">
					{error === "User not found"
						? `@${username} doesn't exist on Macrowave.`
						: "There was an error loading this user profile."}
				</p>
			</div>
		);
	}

	if (!profile) {
		return (
			<div className="flex min-h-[60vh] items-center justify-center">
				<div className="text-center">
					<div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-gray-300 border-b-2" />
					<p className="font-serif text-gray-600">Loading profile...</p>
				</div>
			</div>
		);
	}

	const { broadcaster, stations } = profile;
	const activeStations = stations.filter(
		(station) => station.isActive && station.status === "live",
	);
	const _inactiveStations = stations.filter(
		(station) => !station.isActive || station.status !== "live",
	);

	return (
		<div className="space-y-8">
			{/* User Profile Header */}
			<div className="text-center py-8 border-b border-gray-200">
				<h1 className="font-serif-600 text-3xl text-black mb-2">
					@{broadcaster.username}
				</h1>
				<div className="font-serif text-gray-600">
					<span>
						{stations.length} {stations.length === 1 ? "station" : "stations"}
					</span>
					{activeStations.length > 0 && (
						<span className="ml-3 text-red-500">
							● {activeStations.length} live
						</span>
					)}
				</div>
			</div>

			{/* Stations List */}
			<div className="space-y-4">
				{/* Live Stations */}
				{activeStations.length > 0 && (
					<div>
						<h2 className="mb-4 flex items-center gap-2 font-serif-600 text-xl text-black">
							<div className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
							Live Broadcasts
						</h2>
						<div className="grid gap-4">
							{activeStations.map((station) => (
								<StationCard
									key={station.id}
									onClick={() => {
										getSoundEffects().play("buttonDown");
										// Use new slug-based URLs
										navigate({
											to: "/$username/$stationSlug",
											params: { username, stationSlug: station.slug },
										});
									}}
									station={{
										id: station.id,
										name: station.name,
										slug: station.slug,
										description: station.description,
										isActive: station.isActive,
										viewerCount: station.viewerCount,
										status: station.status,
										nowPlaying: station.nowPlaying,
										broadcasterName: undefined,
									}}
								/>
							))}
						</div>
					</div>
				)}

				{/* All Stations */}
				{stations.length > 0 && (
					<div>
						<h2 className="mb-4 font-serif-600 text-xl text-black">
							{activeStations.length > 0 ? "All Stations" : "Stations"}
						</h2>
						<div className="grid gap-4">
							{stations.map((station) => (
								<StationCard
									key={station.id}
									onClick={() => {
										getSoundEffects().play("buttonDown");
										// Use new slug-based URLs
										navigate({
											to: "/$username/$stationSlug",
											params: { username, stationSlug: station.slug },
										});
									}}
									station={{
										id: station.id,
										name: station.name,
										slug: station.slug,
										description: station.description,
										isActive: station.isActive,
										viewerCount: station.viewerCount,
										status: station.status,
										nowPlaying: station.nowPlaying,
										broadcasterName: undefined,
									}}
								/>
							))}
						</div>
					</div>
				)}

				{/* Empty State */}
				{stations.length === 0 && (
					<div className="py-16 text-center">
						<h3 className="mb-2 font-serif-600 text-xl text-black">
							No Stations Found
						</h3>
						<p className="font-serif text-gray-600">
							@{broadcaster.username} hasn't created any stations yet.
						</p>
					</div>
				)}
			</div>
		</div>
	);
}
